import { AmountData, BotAmountData } from "@shared/features/bots/types";
import {
  AmountType,
  BotData,
  BotStatus,
  BotType,
  ClientData,
  MainCoinAmountData,
  QueryStatus,
} from "@shared/types";
import { DISPLAY_CURRENCIES } from "@shared/common/constants";
import {
  BotFormType,
  PercentageBaseType,
} from "@shared/features/bots/BotForm/common/types";
import { useMemo } from "react";
import { useBotFormAccountAmounts } from "@shared/features/bots/BotForm/common/hooks/useBotFormAccountAmounts";
import {
  getBotAmountData as getOriginalBotAmountData,
  getOptionsItemValue,
} from "@shared/features/bots/utils";

interface useBotAmountProps {
  botType?: BotType;
  botFormType?: BotFormType;
  amountValue?: number;
  amountType?: AmountType;
  amountPercentageBaseType?: PercentageBaseType;
  amountCustomBaseValue?: number;
  account?: ClientData;
  symbol?: string;
  skip?: boolean;
  botsData?: BotData[];
  editBotData?: BotData;
}

const useBotAmount = ({
  botType,
  botFormType,
  amountType,
  amountValue,
  amountPercentageBaseType,
  amountCustomBaseValue,
  symbol,
  account,
  botsData,
  editBotData,
  skip,
}: useBotAmountProps): {
  data: BotAmountData | undefined;
  status: QueryStatus;
  getBotAmountData: (
    args?: Omit<useBotAmountProps, "botType">
  ) => BotAmountData;
} => {
  const isSignalsBot = botType === BotType.SIGNAL;
  const isHedgingBot = botType === BotType.HEDGING;
  const {
    mainCoinsAvailableAmountsData,
    getAccountAmountsData,
    getMainCoinsAmountsData,
    getMainCoinsAvailableAmountDataStatus,
  } = useBotFormAccountAmounts({
    botType,
    botFormType,
    account,
    symbol,
    botsData,
    skip,
  });

  const getSelectedAmount = (
    amountData?: MainCoinAmountData,
    amountType?: AmountType,
    amountValue?: number,
    amountPercentageBaseType?: PercentageBaseType,
    amountCustomBaseValue?: number
  ) => {
    const availableAmount = amountData?.amount || 0;
    let selectedAmount = 0;
    if (amountValue) {
      if (amountType === AmountType.PERCENTAGE) {
        if (amountPercentageBaseType === PercentageBaseType.CUSTOM_AMOUNT) {
          selectedAmount = (amountValue / 100) * (amountCustomBaseValue || 0);
        } else {
          selectedAmount = (amountValue / 100) * (availableAmount || 0);
        }
      } else {
        const amountKey =
          amountType === AmountType.FIXED_USD_AMOUNT
            ? "usd_amount"
            : "btc_amount";
        const availableAmountOfType = amountData?.[amountKey] || 1;
        selectedAmount =
          (amountValue / availableAmountOfType) * (availableAmount || 0);
      }
    }
    return selectedAmount;
  };

  const getBotAmountData = (
    args?: Omit<useBotAmountProps, "botType">
  ): BotAmountData => {
    const {
      symbol,
      amountValue,
      amountType,
      account,
      amountPercentageBaseType,
      amountCustomBaseValue,
      editBotData,
    } = args || {};
    const mainCoinsAvailableAmountsData = isSignalsBot
      ? getAccountAmountsData(account)
      : getMainCoinsAmountsData(symbol, account?.client_id, account?.exchange);
    const {
      convertMainCoinAmountToPercent,
      convertMainCoinAmountToDisplayCurrency:
        baseConvertMainCoinAmountToDisplayCurrency,
      mainCoinAvailableAmount,
      mainCoinAmountData,
      mainCoinEffectiveTotalAmountData,
      mainCoin,
    } = mainCoinsAvailableAmountsData;

    const shouldUseEffectiveTotalAmount =
      !!botFormType &&
      !isSignalsBot &&
      amountType === AmountType.PERCENTAGE &&
      amountPercentageBaseType !== PercentageBaseType.CUSTOM_AMOUNT &&
      !!mainCoinEffectiveTotalAmountData;
    const selectedAmountData = shouldUseEffectiveTotalAmount
      ? mainCoinEffectiveTotalAmountData
      : mainCoinAmountData;
    const convertMainCoinAmountToDisplayCurrency = (
      mainCoinAmount: number,
      displayCurrency: Parameters<
        typeof baseConvertMainCoinAmountToDisplayCurrency
      >[1],
      applyPrecision?: boolean
    ) => {
      return baseConvertMainCoinAmountToDisplayCurrency(
        mainCoinAmount,
        displayCurrency,
        applyPrecision,
        selectedAmountData
      );
    };

    const convertMainCoinAmountToAmountType = (
      mainCoinAmount: number,
      applyPrecision?: boolean,
      _amountType?: AmountType
    ) => {
      const botAmountType = _amountType ?? amountType;
      if (botAmountType === AmountType.PERCENTAGE) {
        return convertMainCoinAmountToPercent(mainCoinAmount, applyPrecision);
      }
      const displayCurrency =
        botAmountType === AmountType.FIXED_USD_AMOUNT
          ? DISPLAY_CURRENCIES.USD
          : DISPLAY_CURRENCIES.BTC;
      return convertMainCoinAmountToDisplayCurrency(
        mainCoinAmount,
        displayCurrency,
        applyPrecision
      );
    };

    const getAmountData = (mainCoinAmount: number): AmountData => {
      return {
        mainCoin: mainCoinAmount,
        BTCAmount:
          convertMainCoinAmountToDisplayCurrency(
            mainCoinAmount,
            DISPLAY_CURRENCIES.BTC
          ) || 0,
        USDAmount:
          convertMainCoinAmountToDisplayCurrency(
            mainCoinAmount,
            DISPLAY_CURRENCIES.USD
          ) || 0,
      };
    };

    const getAmountDataFromMainCoinAmountData = (
      amountData?: MainCoinAmountData
    ): AmountData => {
      return {
        mainCoin: amountData?.amount || 0,
        BTCAmount: amountData?.btc_amount || 0,
        USDAmount: amountData?.usd_amount || 0,
      };
    };

    const subtractAmountData = (
      amountData?: AmountData,
      amountToSubtract?: AmountData
    ): AmountData => {
      return {
        mainCoin:
          (amountData?.mainCoin || 0) - (amountToSubtract?.mainCoin || 0),
        BTCAmount:
          (amountData?.BTCAmount || 0) - (amountToSubtract?.BTCAmount || 0),
        USDAmount:
          (amountData?.USDAmount || 0) - (amountToSubtract?.USDAmount || 0),
      };
    };

    const getOriginalEditedBotMainCoinAmount = () => {
      const isEditForm =
        botFormType === BotFormType.EDIT ||
        botFormType === BotFormType.BULK_EDIT;
      if (
        !isHedgingBot ||
        !isEditForm ||
        editBotData?.status !== BotStatus.ACTIVE ||
        !editBotData?.number_of_open_trades
      ) {
        return 0;
      }
      const {
        amountType: originalAmountType,
        amountValue: originalAmountValue,
      } = getOriginalBotAmountData(editBotData);
      return getSelectedAmount(
        mainCoinAmountData,
        originalAmountType,
        originalAmountValue,
        getOptionsItemValue(editBotData, "Amount Percentage Base Type") as
          | PercentageBaseType
          | undefined,
        getOptionsItemValue(editBotData, "Amount Custom Base Value") as
          | number
          | undefined
      );
    };

    const mainCoinAmount = getSelectedAmount(
      selectedAmountData,
      amountType,
      amountValue,
      amountPercentageBaseType,
      amountCustomBaseValue
    );
    const adjustedMainCoinAvailableAmount =
      (mainCoinAvailableAmount || 0) + getOriginalEditedBotMainCoinAmount();
    const availableAmount = getAmountData(adjustedMainCoinAvailableAmount);
    const effectiveTotalAmount = mainCoinEffectiveTotalAmountData
      ? getAmountDataFromMainCoinAmountData(mainCoinEffectiveTotalAmountData)
      : undefined;
    const rawAvailableAmount =
      getAmountDataFromMainCoinAmountData(mainCoinAmountData);
    const openTradesAmount = effectiveTotalAmount
      ? subtractAmountData(effectiveTotalAmount, rawAvailableAmount)
      : undefined;

    return {
      ...mainCoinsAvailableAmountsData,
      mainCoinAvailableAmount: adjustedMainCoinAvailableAmount,
      mainCoinAmount,
      convertMainCoinAmountToDisplayCurrency,
      convertMainCoinAmountToAmountType,
      availableAmount,
      effectiveTotalBreakdown: shouldUseEffectiveTotalAmount
        ? {
            availableAmount,
            openTradesAmount: openTradesAmount || getAmountData(0),
            totalAmount: effectiveTotalAmount || getAmountData(0),
            selectedAmount: getAmountData(mainCoinAmount),
            percentage: amountValue || 0,
          }
        : undefined,
      initialized: !!mainCoin && !!account && (isSignalsBot || !!symbol),
      getSelectedAmount,
      amountType,
      amountValue,
      amountPercentageBaseType,
      amountCustomBaseValue,
    };
  };

  const data = useMemo(() => {
    if (!getMainCoinsAvailableAmountDataStatus.isSuccess) return;
    return getBotAmountData({
      symbol,
      account,
      amountValue,
      amountType,
      amountPercentageBaseType,
      amountCustomBaseValue,
      editBotData,
    });
  }, [
    botType,
    symbol,
    account,
    amountValue,
    amountType,
    amountPercentageBaseType,
    amountCustomBaseValue,
    editBotData,
    botFormType,
    mainCoinsAvailableAmountsData,
    getMainCoinsAvailableAmountDataStatus.data,
    getMainCoinsAvailableAmountDataStatus.isFetching,
    getMainCoinsAvailableAmountDataStatus.isSuccess,
  ]);

  return {
    data,
    getBotAmountData,
    status: getMainCoinsAvailableAmountDataStatus,
  };
};

export default useBotAmount;
